import type { InstructorCheckInDTO } from "@/modules/master/domain/contracts";
import { formatDateTime } from "@/shared/lib/date-time";
import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";

type InstructorCheckInsTableProps = {
  data: InstructorCheckInDTO[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, _limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
};

export function InstructorCheckInsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
}: InstructorCheckInsTableProps) {
  const columns: TanstackReactTable.ColumnDef<InstructorCheckInDTO>[] = [
    createSortableColumn("checkedInAt", "ວັນທີ & ເວລາ Check-in", {
      cell: ({ row }) => (
        <div>
          <div className="font-semibold">
            {formatDateTime(row.original.checkedInAt)}
          </div>
        </div>
      ),
    }),
    {
      header: "ປະເພດ",
      accessorKey: "sessionType",
      cell: ({ row }) => {
        const type = row.original.sessionType;
        const variant =
          type === "general_class"
            ? "default"
            : type === "room_booking"
              ? "outline"
              : "secondary";
        const label =
          type === "general_class"
            ? "ຊົ່ວໂມງຮຽນຄລາດສ໌"
            : type === "room_booking"
              ? "Room Assist"
              : "Private Lesson";
        return <Badge variant={variant}>{label}</Badge>;
      },
    },
    {
      header: "ຊື່ຊົ່ວໂມງຮຽນ / ການຈອງ",
      accessorKey: "sessionName",
      cell: ({ row }) => (
        <div className="font-semibold">{row.original.sessionName}</div>
      ),
    },
    {
      header: "ຜູ້ Check-in",
      accessorKey: "checkedInByName",
      cell: ({ row }) => row.original.checkedInByName || "-",
    },
    {
      header: "ສະຖານະ",
      accessorKey: "isSubstitute",
      cell: ({ row }) => {
        const isSubstitute = row.original.isSubstitute;
        if (!isSubstitute) {
          return <span className="text-muted-foreground text-sm">-</span>;
        }
        return (
          <div className="flex flex-col gap-1">
            <Badge
              variant="outline"
              className="w-fit border-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
            >
              ສອນແທນ
            </Badge>
            {row.original.substituteForInstructorName && (
              <span className="text-muted-foreground text-xs">
                ແທນ: {row.original.substituteForInstructorName}
              </span>
            )}
            {row.original.substituteReason && (
              <span className="text-muted-foreground text-xs">
                {row.original.substituteReason}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
